<script setup lang="ts">
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { computed, ref } from 'vue'
  import type { PlanAccordeonItem } from '../../FlowSelectPlanWidget.types'

  interface Props {
    item: PlanAccordeonItem
  }

  interface Emits {
    (e: 'click-learn-more'): void
    (e: 'toggle', isOpen: boolean): void
  }

  const props = defineProps<Props>()

  const emit = defineEmits<Emits>()

  const isOpen = ref(false)

  const toggleCoverage = () => {
    if (!canOpen.value) return
    isOpen.value = !isOpen.value
    emit('toggle', isOpen.value)
  }

  const canOpen = computed(() => props.item.coverage?.length && props.item.coverage.length > 0)
</script>

<template>
  <div
    class="single-plan-accordion__item"
    :class="{
      'single-plan-accordion__item--open': isOpen,
      'single-plan-accordion__item--non-interactive': !canOpen,
    }"
  >
    <div class="single-plan-accordion__item-header" @click="toggleCoverage">
      <img :src="props.item.icon" class="single-plan-accordion__item-icon" />
      <div class="single-plan-accordion__item-content">
        <div class="heading-6">{{ $t(item.title) }}</div>
        <div class="text-regular">{{ $t(item.annotation) }}</div>
      </div>
      <button
        v-if="canOpen"
        class="single-plan-accordion__item-button"
        data-test="learn-more-button"
      >
        <IconQC name="chevron-down" color="var(--colorIcnSrfSecondary)" size="lg" />
      </button>
    </div>
    <Transition name="fade">
      <div v-if="isOpen" class="single-plan-accordion__coverage">
        <div class="single-plan-accordion__coverage-header text-small-regular">
          <div>{{ $t('outbound.select_plan.insurance_values.coverage') }}</div>
          <div>{{ $t('outbound.select_plan.insurance_values.payout_limit') }}</div>
        </div>
        <div>
          <div
            v-for="coverage in props.item.coverage"
            :key="coverage.title"
            class="single-plan-accordion__coverage-item"
          >
            <IconQC name="check-circle-fill" color="var(--colorIcnSrfAction)" size="lg" />
            <div class="text-regular">
              {{ $t(coverage.title) }}
            </div>
            <div class="text-regular">
              {{ $t(coverage.price) }}
            </div>
          </div>
        </div>
        <ButtonQC
          variant="outline"
          size="small"
          class="single-plan-accordion__coverage-button"
          @click="emit('click-learn-more')"
          >{{ $t('qic.common.learn_more') }}</ButtonQC
        >
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils' as *;

  .single-plan-accordion__item {
    padding: var(--spacing-7);
    padding-bottom: 0;
    border: 1px solid var(--colorBrdrSrfSecondary);
    border-bottom: 0;

    &:first-child {
      border-start-start-radius: var(--spacing-5);
      border-start-end-radius: var(--spacing-5);
    }

    &:last-child {
      border-end-start-radius: var(--spacing-5);
      border-end-end-radius: var(--spacing-5);
      border-bottom: 1px solid var(--colorBrdrSrfSecondary);
    }

    &.single-plan-accordion__item--non-interactive {
      .single-plan-accordion__item-header {
        cursor: default;
      }
    }

    @include mobile-flow {
      padding: var(--spacing-6) var(--spacing-5);
    }
  }

  .single-plan-accordion__item-header {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-5);
    cursor: pointer;
    padding-bottom: var(--spacing-7);

    @include mobile-flow {
      padding-bottom: var(--spacing-6);
    }
  }

  .single-plan-accordion__item-icon {
    width: 36px;
    height: 36px;
  }

  .single-plan-accordion__item-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);
  }

  .single-plan-accordion__item-button {
    background: none;
    border: none;
    cursor: pointer;
    transition: transform 0.3s;
    margin-inline-start: auto;
  }

  .single-plan-accordion__coverage {
    display: flex;
    padding-block: var(--spacing-6) var(--spacing-8);
    flex-direction: column;
    border-top: 1px solid var(--colorBrdrSrfSecondary);

    @include mobile-flow {
      padding-block: var(--spacing-5) var(--spacing-6);
    }
  }

  .single-plan-accordion__coverage-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--colorTxtSrfSecondary);
    padding-block: var(--spacing-2);
  }

  .single-plan-accordion__coverage-item {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--spacing-3);
    padding-block: var(--spacing-2);
  }

  .single-plan-accordion__coverage-button {
    margin: var(--spacing-5) auto 0 auto;
    width: fit-content;
  }

  .single-plan-accordion__item--open {
    .single-plan-accordion__item-button {
      transform: rotate(180deg);
    }
  }
</style>
